import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreditDebugUtils } from "@/utils/creditDebug";
import { useRepairShopContext } from "@/context/RepairShopContext";

export const CreditTestComponent: React.FC = () => {
  const { repairShop } = useRepairShopContext();
  const [clientId, setClientId] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState(100);
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const debugClient = async () => {
    if (!clientId.trim()) return;
    setLoading(true);
    try {
      const result = await CreditDebugUtils.debugClientCredit(clientId);
      setResults(result);
    } catch (error) {
      console.error("Debug error:", error);
    } finally {
      setLoading(false);
    }
  };

  const testInvoiceCreation = async () => {
    if (!clientId.trim()) return;
    setLoading(true);
    try {
      const result = await CreditDebugUtils.testInvoiceCreation(
        clientId,
        invoiceAmount
      );
      setResults(result);
    } catch (error) {
      console.error("Test error:", error);
    } finally {
      setLoading(false);
    }
  };

  const listAllClients = async () => {
    if (!repairShop) return;
    setLoading(true);
    try {
      await CreditDebugUtils.listAllClientsCredit(repairShop.id);
    } catch (error) {
      console.error("List error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Credit Management Debug Tool</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Client ID</label>
            <Input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Enter client ID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Invoice Amount
            </label>
            <Input
              type="number"
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(Number(e.target.value))}
              placeholder="100"
            />
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={debugClient} disabled={loading || !clientId.trim()}>
            Debug Client Credit
          </Button>
          <Button
            onClick={testInvoiceCreation}
            disabled={loading || !clientId.trim()}
          >
            Test Invoice Creation
          </Button>
          <Button onClick={listAllClients} disabled={loading}>
            List All Clients
          </Button>
        </div>

        {results && (
          <div className="mt-4 p-4 bg-gray-100 rounded">
            <h3 className="font-bold mb-2">Results:</h3>
            <pre className="text-sm overflow-auto">
              {JSON.stringify(results, null, 2)}
            </pre>
          </div>
        )}

        <div className="text-sm text-gray-600">
          <p>
            <strong>Instructions:</strong>
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              Enter a client ID and click "Debug Client Credit" to see current
              credit status
            </li>
            <li>
              Click "Test Invoice Creation" to create a test invoice and see if
              credit updates
            </li>
            <li>
              Click "List All Clients" to see all clients' credit info in
              console
            </li>
            <li>Check browser console for detailed logs</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
